import React, { useCallback, useEffect, useRef, useState } from 'react';
import ModeDeliveryTableFragment from './component/ModelDeliveryFragment';
import HpsParcelSectionWithData from './component/HpsParcelSectionWithData';

import { Row, Col, Card, CardBody, Label, Alert } from 'reactstrap';

import 'react-datepicker/dist/react-datepicker.css';
import 'flatpickr/dist/themes/material_blue.css';
import 'flatpickr/dist/plugins/monthSelect/style.css';
import axios from 'axios';
import { api } from '../../config';
import notify from '../../utils/notify';
import { confirmSubmit } from '../../utils/confirm';
import { format } from 'date-fns';
import logger from '../../utils/logger';
import { formatDateToDDMMYYYY } from '../../utils/dateformatter';
import {
  displayValue,
  isSafeExternalUrl,
  canEditHpsAlpha,
  isHpsNotAvailable,
  isHpsRevise,
  getHpsStatusKind,
} from './utils/hpsHelpers';
import {
  downloadParcelExcel,
  downloadParcelExcelLpg,
  orderParcelsByDisplayOrder,
  reorderParcelsByDetail,
  buildDetailOrderIndexFromGroups,
  enrichParcelWithTimeline,
} from './utils/parcelExportHelpers';
import {
  collectMissingNonLpgAlpha,
  collectMissingLpgAlpha,
  formatMissingAlphaMessage,
} from './utils/hpsSubmitValidation';
import {
  getIncotermBasesFromParcel,
  loadCargoRatioTable,
} from './component/HpsParcel/lpg/utils';
import UploadAlphaHpsModal from './component/UploadAlphaHpsModal';
import UploadNonLPGModal from './component/UploadNonLPGModal';
import NotaDinasRilisUpload from './component/NotaDinasRilisUpload';
import '../../App.css';
import './HpsView.css';
import ActionButton from '../../Components/Common/ActionButton';
import { SkeletonCard } from '../../Components/Common/Skeleton';
import {
  getProcurementProductTypeNames,
  requiresLpgCargoRatioForCargo,
} from '../../shared/offer-domain/constants';
import ParcelField from './component/HpsParcel/shared/ParcelField';
import HpsRevisePanel from './component/HpsRevisePanel';
import { mapParcelPricingDisplay } from '../../Components/QuantityTable/utils/pricingPeriodHelpers';
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = 'XSRF-TOKEN';
axios.defaults.xsrfHeaderName = 'X-XSRF-TOKEN';
const LPG_PRODUCT = ['4588b5e6-7309-49d0-9107-59cbb96b6bd8'];

const InfoField = ({ label, value, error }) => (
  <Col lg={4}>
    <div className='mb-2 field-border-brown'>
      <Label for='contractType' className='text-muted'>
        {label}
      </Label>
      <p className='mb-0 text-dark fw-medium'>{displayValue(value)}</p>
      {error && <div className='text-danger small mt-1'>{error}</div>}
    </div>
  </Col>
);

const openDocument = url => {
  if (!isSafeExternalUrl(url)) return;
  window.open(url, '_blank', 'noopener,noreferrer');
};

const RequirementTab = ({
  ids,
  form,
  errors,
  error,
  isLoading,
  qtyDeliveryData,
  token,
  onRefreshForm,
}) => {
  const quantityRefs = useRef();

  const notaDinasRilisRef = useRef(null);
  const [notaDinasRilisError, setNotaDinasRilisError] = useState('');
  const [isDownloadingParcel, setIsDownloadingParcel] = useState(false);
  const [isUploadAlphaHpsOpen, setIsUploadAlphaHpsOpen] = useState(false);
  const toggleUploadAlphaHps = () => setIsUploadAlphaHpsOpen(prev => !prev);
  const [alphaUploadVersion, setAlphaUploadVersion] = useState(0);

  const [isUploadNonLPGOpen, setIsUploadNonLPGOpen] = useState(false);
  const alphaEditable = canEditHpsAlpha(form.statusHPS2);
  const hpsNotAvailable = isHpsNotAvailable(form.statusHPS2);
  const hpsRevise = isHpsRevise(form.statusHPS2);
  const toggleUploadNonLPG = () => setIsUploadNonLPGOpen(prev => !prev);
  const [nonLPGUploadVersion, setNonLPGUploadVersion] = useState(0);

  // get-delivery-parcel tidak menyertakan infoPrompt (field pricing hidup di
  // /quantity-delivery), jadi enrich dari qtyDeliveryData yang di-match per id
  // delivery unit — parcel.id memang dipakai sebagai quantityDeliveryTermId.
  const enrichParcelsWithInfoPrompt = parcels =>
    (Array.isArray(parcels) ? parcels : []).map(parcel => {
      if (parcel?.infoPrompt !== undefined) return parcel;
      const matched = qtyDeliveryData.items.find(
        item => String(item.id) === String(parcel?.id)
      );
      return { ...parcel, infoPrompt: matched?.infoPrompt ?? null };
    });

  const unwrapParcelResponse = res => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    return [];
  };

  // Urutkan listData + lokasi FOB tiap parcel sesuai get-delivery-detail
  // (sama dengan tabel Incoterm / qtyModeDeliveryIncoterm di UI).
  const reorderParcelsToDetailOrder = async parcels => {
    if (!parcels?.length) return parcels;
    const indexByTermId = {};
    await Promise.all(
      parcels.map(async parcel => {
        try {
          const res = await axios.get(
            `${api.API_URL}/procurement/get-delivery-detail?PageNumber=1&PageSize=500&procurementId=${ids}&quantityDeliveryTermId=${parcel.id}`,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
            }
          );
          const unwrapDeliveryDetailGroups = res => {
            if (Array.isArray(res)) return res;
            if (Array.isArray(res?.data)) return res.data;
            if (Array.isArray(res?.data?.data)) return res.data.data;
            if (Array.isArray(res?.data?.Data)) return res.data.Data;
            return [];
          };
          const groups = unwrapDeliveryDetailGroups(res);
          indexByTermId[String(parcel?.id)] =
            buildDetailOrderIndexFromGroups(groups);
        } catch {
          // fallback: pertahankan urutan get-delivery-parcel
        }
      })
    );
    return reorderParcelsByDetail(parcels, indexByTermId);
  };

  const handleDwnParcelLpg = async () => {
    if (!ids || !token) return;
    setIsDownloadingParcel(true);
    try {
      const res = await axios.get(
        `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${ids}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const parcels = orderParcelsByDisplayOrder(
        enrichParcelsWithInfoPrompt(unwrapParcelResponse(res)),
        qtyDeliveryData.items
      );
      const ok = await downloadParcelExcelLpg(
        parcels,
        ids,
        form?.procurementNumber,
        token
      );
      if (!ok) {
        notify.info('There is no parcel data to download.');
      }
    } catch (err) {
      logger.error(
        'Error downloading parcel:',
        err?.response?.status ?? 'no-status',
        err?.message ?? String(err)
      );
      notify.apiError(err, 'download parcel data');
    } finally {
      setIsDownloadingParcel(false);
    }
  };

  const handleDownloadParcel = async () => {
    if (!ids || !token) return;
    setIsDownloadingParcel(true);
    try {
      const res = await axios.get(
        `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${ids}`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const orderedByDisplay = orderParcelsByDisplayOrder(
        enrichParcelsWithInfoPrompt(unwrapParcelResponse(res)),
        qtyDeliveryData.items
      );
      const parcels = await reorderParcelsToDetailOrder(orderedByDisplay);
      const ok = downloadParcelExcel(parcels, form?.procurementNumber);
      if (!ok) {
        notify.info('There is no parcel data to download.');
      }
    } catch (err) {
      logger.error(
        'Error downloading parcel:',
        err?.response?.status ?? 'no-status',
        err?.message ?? String(err)
      );
      notify.apiError(err, 'download parcel data');
    } finally {
      setIsDownloadingParcel(false);
    }
  };

  const [isendDate, setIsEndDate] = useState(false);
  const [, setUnitSizeType] = useState('');
  const [productName, setProductName] = useState({ items: [] });
  const [productTypeData, setProductTypeData] = useState({ items: [] });
  const [, setInvoiceQuantityData] = useState({ items: [] });
  const [, setModelInvoiceQuantityData] = useState({
    items: [],
  });
  const [, setIncotermRouteData] = useState({ items: [] });
  const [, setIncotremType] = useState({ items: [] });
  const [, setButaneSection] = useState(false);
  const [, setPropaneSection] = useState(false);
  const [, setCompTile] = useState('');
  const [, setCompTileSec] = useState('');
  const [, setActiveTabs] = useState({});

  useEffect(() => {
    quantityRefs.current = qtyDeliveryData.items.map(() => React.createRef());
  }, [qtyDeliveryData.items]);

  useEffect(() => {
    if (form.productTypeId) {
      CompositionVar(form.productTypeId);
    }
  }, [form.productTypeId]);

  const fetchProductName = useCallback(async () => {
    try {
      const response = await fetch(`${api.API_URL}/product`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setProductName({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  const fetchInvoiceQuantity = useCallback(async () => {
    try {
      const response = await fetch(`${api.API_URL}/invoicing-quantity`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setInvoiceQuantityData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  const fetchModeInvoiceQuantity = useCallback(
    async invoicingQuantityId => {
      try {
        const response = await fetch(
          `${api.API_URL}/invoicing-quantity/getAll-ModeInvoicingQuantity?InvoicingQuantityId=${invoicingQuantityId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();
        const items = result.data.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setModelInvoiceQuantityData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    },
    [token]
  );

  const fetchProductType = useCallback(
    async productTypeId => {
      try {
        const response = await fetch(
          `${api.API_URL}/product/getAll-product-type?ProductId=${productTypeId}`,
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await response.json();
        const items = result.data.items.map(item => ({
          value: item.id,
          label: item.name,
        }));
        setProductTypeData({ items });
      } catch (error) {
        logger.error('Error:', error);
      }
    },
    [token]
  );

  const CompositionVar = action => {
    //let comptitle = "";
    switch (action) {
      case '4588b5e6-7309-49d0-9107-59cbb96b6b11':
        setCompTileSec('Refrigerated Propane');
        setPropaneSection(true);
        setButaneSection(false);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b12':
        setCompTile('Refrigerated Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;

      case '4588b5e6-7309-49d0-9107-59cbb96b6b13':
        setCompTile('LPG Mixed Pressurized');
        setButaneSection(true);
        setPropaneSection(false);
        break;

      case '4588b5e6-7309-49d0-9107-59cbb96b6b19':
        setCompTile('Pressurized Butane');
        setCompTileSec('Pressurized Propane');
        setButaneSection(true);
        setPropaneSection(true);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b18':
        setCompTile('Refrigrate Butane');
        setCompTileSec('Refrigrate Propane');
        setButaneSection(true);
        setPropaneSection(true);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b15':
        setCompTile('Pressurized Butane');
        setButaneSection(true);
        setPropaneSection(false);
        break;
      case '4588b5e6-7309-49d0-9107-59cbb96b6b14':
        setCompTileSec('Pressurized Propane');
        setPropaneSection(true);
        setButaneSection(false);
        break;
      //comptitle = "None";
    }
    //setCompTile(comptitle);
  };

  const [, setDeliveryFormData] = useState({
    procurementId: null,
    procurement: null,
    deliveryUnit: null,
    frequency: null,
    ratioLpgId: null,
    ratioLpg: null,
    pricingPeriod: null,
    indexPriceId: null,
    indexPrice: null,
    otherIndexPriceId: null,
    otherIndexPrice: null,
    isCorrelationBetweenTwoIndex: null,
    startDatePublishIndex: null,
    endDatePublishIndex: null,
    schemaBL: null,
    infoPrompt: null,
    notesForPricing: null,
    id: null,
  });

  useEffect(() => {
    const newTabs = {};
    qtyDeliveryData.items.forEach(item => {
      newTabs[item.id] = `${item.id}1`;
    });
    setActiveTabs(newTabs);
  }, [qtyDeliveryData.items]);

  ///------- Quantity delivery

  ///--- Add Incoterm---------------------------------------------------------------

  const fetchIncotermRoute = useCallback(async () => {
    try {
      const response = await fetch(
        `${api.API_URL}/incoterm/get-incoterm-route`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setIncotermRouteData({ items });
    } catch (error) {
      logger.error('Error:', error);
    }
  }, [token]);

  const fetchIncotermType = useCallback(async () => {
    try {
      const response = await fetch(`${api.API_URL}/incoterm `, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      const items = result.data.items.map(item => ({
        value: item.id,
        label: item.name,
      }));
      setIncotremType({ items });
    } catch (error) {
      logger.error('Error index price:', error);
    }
  }, [token]);

  useEffect(() => {
    fetchProductName();
    fetchInvoiceQuantity();
    fetchIncotermRoute();
    fetchIncotermType();
    setDeliveryFormData(prev => ({
      ...prev,
      procurementId: form.id,
    }));
    setUnitSizeType(
      form.productNameId === '4588b5e6-7309-49d0-9107-59cbb96b6bd8'
        ? 'MT'
        : 'MB'
    );
    setIsEndDate(parseInt(form.contractType, 10) === 2);
  }, [
    form.id,
    form.productNameId,
    form.contractType,
    fetchProductName,
    fetchInvoiceQuantity,
    fetchIncotermRoute,
    fetchIncotermType,
  ]);

  useEffect(() => {
    if (form.invoicingQuantityId) {
      fetchModeInvoiceQuantity(form.invoicingQuantityId);
    }
  }, [form.invoicingQuantityId, fetchModeInvoiceQuantity]);

  useEffect(() => {
    if (form.productNameId) {
      fetchProductType(form.productNameId);
    }
  }, [form.productNameId, fetchProductType]);

  // Validasi alpha non-LPG: fetch get-delivery-parcel, cek alphaDyed/Undyed
  // per listData + per lokasi FOB (skip kolom N/A).
  const validateAlphaNonLpg = async () => {
    const res = await axios.get(
      `${api.API_URL}/procurement/get-delivery-parcel?PageNumber=1&PageSize=100&procurementId=${ids}`,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const parcels = orderParcelsByDisplayOrder(
      enrichParcelsWithInfoPrompt(unwrapParcelResponse(res)),
      qtyDeliveryData.items
    );
    return collectMissingNonLpgAlpha(parcels);
  };

  // Validasi alpha LPG: rebuild model tabel per parcel (sumber sama dengan UI:
  // loadCargoRatioTable) lalu cek row.alpha (kosong = 'Requested'/0/null/dst).
  const validateAlphaLpg = async () => {
    const missing = [];
    await Promise.all(
      qtyDeliveryData.items.map(async item => {
        try {
          const parcel = await enrichParcelWithTimeline(item, token);
          const incotermBases = getIncotermBasesFromParcel(parcel);
          if (!incotermBases.length) return;
          const requiresCargoRatio = requiresLpgCargoRatioForCargo(form, item);
          const { tableModel } = await loadCargoRatioTable({
            procurementId: ids,
            quantityDeliveryTermId: item.id,
            incotermBases,
            unitLabel: form?.productType?.unit || 'MT',
            skipCargoRatio: !requiresCargoRatio,
          });
          if (requiresCargoRatio && !tableModel?.hasCargoRatioSelection) {
            missing.push({
              deliveryUnit: item.deliveryUnit || item.id || '-',
              field: 'cargoRatio',
              label: 'Cargo Ratio not selected',
            });
            return;
          }
          const rows = (tableModel?.rows || []).map(row => ({
            ...row,
            deliveryUnit: item.deliveryUnit || item.id || '-',
          }));
          missing.push(...collectMissingLpgAlpha(rows));
        } catch (err) {
          logger.error(
            'Error validating LPG alpha:',
            err?.response?.status ?? 'no-status',
            err?.message ?? String(err)
          );
          throw err;
        }
      })
    );
    return missing;
  };

  const validateAlphaBeforeSubmit = async () => {
    try {
      const isLpg = LPG_PRODUCT.includes(form.productNameId);
      const missing = isLpg
        ? await validateAlphaLpg()
        : await validateAlphaNonLpg();
      if (missing.length) {
        notify.warning(
          formatMissingAlphaMessage(
            missing,
            isLpg
              ? 'Alpha and Cargo Ratio'
              : 'Alpha Dyed/Undyed for every parcel'
          )
        );
        return false;
      }
      return true;
    } catch (err) {
      notify.apiError(err, 'validate alpha HPS');
      return false;
    }
  };

  const handleConfirmProcess = async () => {
    if (!form.documentNotaDinasRilis) {
      const message =
        'Please upload Document Nota Dinas Rilis before submitting HPS.';
      setNotaDinasRilisError(message);
      notify.warning(message);
      notaDinasRilisRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    if (!(await validateAlphaBeforeSubmit())) return;
    if (
      !(await confirmSubmit('HPS request', undefined, {
        message:
          'The HPS request will be sent on for processing. This cannot be undone.',
      }))
    )
      return;
    await handleSaveReleasedHps(ids);
    // Reload form (fetchDataProcurement) agar status/data aktual setelah submit.
    await onRefreshForm?.();
  };

  const handleSaveReleasedHps = async ids => {
    try {
      const response = await axios.post(
        `${api.API_URL}/hps/update-status-availability`,
        null,
        {
          params: { procurementId: ids },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, // jika ada token
          },
        }
      );
      logger.log('Response:', response.data);
    } catch (error) {
      notify.apiError(error, 'submit HPS request');
    }
  };

  //----

  return (
    <React.Fragment>
      <Row>
        <div>
          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <div
                    color='dark'
                    className='d-flex align-items-center mb-2  '
                  >
                    <div className='flex-grow-1'>
                      <h5 className='mb-1'>
                        <i className='ri-file-list-3-line me-2'></i>
                        Procurement Information
                      </h5>
                      <p className='text-muted mb-2 fs-12 '>
                        Bisa jadi Kalimat tambahan untuk membantu section
                        mengenai apa ini
                      </p>
                    </div>
                  </div>
                </Col>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Procurement Type :
                    </Label>
                    <p className='form-label mb-2 fs-13 '>
                      {form?.procurementTypeHeaderLabel}
                    </p>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Title Procurement :
                    </Label>
                    <p className='form-label mb-2 fs-13 '>
                      {form?.titleProcurement}
                    </p>
                  </div>
                </Col>
                <Col lg={4}>
                  {!isendDate && (
                    <div className='mb-2 field-border-brown'>
                      <Label for='contractPeriodTermEnd' className='text-muted'>
                        {' '}
                        Proc Type{' :'}
                      </Label>

                      <p className='form-label mb-3 fs-12 '>
                        {form.procurementType}
                      </p>
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Contract Type :
                    </Label>
                    <p className='form-label mb-2 fs-13 '>
                      {parseInt(form?.contractType ?? 0, 10) === 1
                        ? 'TERM'
                        : 'SPOT'}
                    </p>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractPeriodTermStart' className='text-muted'>
                      {!isendDate ? 'Start Period :' : 'Period :'}{' '}
                    </Label>
                    <p className='form-label mb-3 fs-12 '>
                      {form.contractPeriodTermStart
                        ? format(
                            new Date(form.contractPeriodTermStart),
                            'MMMM yyyy'
                          )
                        : '-'}
                    </p>
                  </div>
                </Col>
                <Col lg={4}>
                  {!isendDate && (
                    <div className='mb-2 field-border-brown'>
                      <Label for='contractPeriodTermEnd' className='text-muted'>
                        {' '}
                        End Period{' :'}
                      </Label>

                      <p className='form-label mb-3 fs-12 '>
                        {form.contractPeriodTermEnd
                          ? format(
                              new Date(form.contractPeriodTermEnd),
                              'MMMM yyyy'
                            )
                          : '-'}
                      </p>
                    </div>
                  )}
                </Col>
              </Row>

              <Row>
                <Col sm={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label className='text-muted'>Product Name :</Label>
                    <p className='form-label mb-2 fs-13 '>
                      {productName.items?.find(
                        obj => obj.value === form.productNameId
                      )?.label || '-'}
                    </p>
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2 field-border-brown'>
                    <p className='form-label '>Product Type </p>

                    <p className='text-muted mb-2 fs-12 '>
                      {getProcurementProductTypeNames(form) ||
                        productTypeData.items?.find(
                          obj => obj.value === form.productTypeId
                        )?.label ||
                        '-'}
                    </p>
                  </div>
                </Col>

                <Col sm={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractPeriodTermStart' className='form-label'>
                      Operational Tolerance{' '}
                    </Label>

                    <p className='form-label mb-2 fs-12 '>
                      {form.operationalTolerance || ''}
                      {' %'}
                    </p>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                <Col sm={12}>
                  <div
                    color='dark'
                    className='d-flex align-items-center mb-2  '
                  >
                    <div className='flex-grow-1'>
                      <h5 className='mb-1'>Request HPS</h5>
                      <p className='text-muted mb-2 fs-12 '>
                        Bisa jadi Kalimat tambahan untuk membantu section
                        mengenai apa ini
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      HPS Request Numbers :
                    </Label>
                    <p className='form-label mb-2 fs-13 '>
                      {form?.hpsRequestNumber || '-'}
                    </p>
                  </div>
                </Col>

                <Col lg={4}>
                  <div className='mb-2 field-border-brown'>
                    <Label for='contractType' className='text-muted'>
                      Request Date :
                    </Label>
                    <p className='form-label mb-2 fs-13 '>
                      {formatDateToDDMMYYYY(form?.hpsRequestDate) || '-'}
                    </p>
                  </div>
                </Col>
              </Row>

              <Row className='g-3'>
                <>
                  <InfoField
                    label='Title Nota Dinas'
                    value={form.titleNotaDinas}
                    error={errors.titleNotaDinas}
                  />
                  <InfoField
                    label='No. Nota Dinas'
                    value={form.noNotaDinas}
                    error={errors.noNotaDinas}
                  />
                  <InfoField
                    label='Date Nota Dinas'
                    value={
                      form.hpsRequestDate
                        ? formatDateToDDMMYYYY(form.hpsRequestDate)
                        : '-'
                    }
                    error={errors.hpsRequestDate}
                  />
                  <Col md={12} lg={12}>
                    <div className='mb-3'>
                      <Label className='fw-semibold small text-dark mb-2'>
                        Document Nota Dinas
                      </Label>

                      <div className='border rounded-3 p-2 d-flex align-items-center justify-content-between bg-light'>
                        <div className='d-flex align-items-center overflow-hidden me-2'>
                          <div
                            className='d-flex align-items-center justify-content-center rounded bg-primary-subtle p-2 me-2 text-primary'
                            style={{
                              width: '36px',
                              height: '36px',
                              flexShrink: 0,
                            }}
                          >
                            <i className='ri-file-text-line fs-20'></i>
                          </div>

                          <span className='small text-dark fw-medium text-break'>
                            {(() => {
                              const rawName =
                                form?.documentNotaDinasName ||
                                form?.documentNotaDinas;
                              if (!rawName) return 'Nota Dinas Document';
                              return rawName.includes('_')
                                ? rawName.substring(rawName.indexOf('_') + 1)
                                : rawName;
                            })()}
                          </span>
                        </div>

                        {form?.documentNotaDinasUrl && (
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-eye-line'
                            onClick={() =>
                              openDocument(form.documentNotaDinasUrl)
                            }
                          >
                            View
                          </ActionButton>
                        )}
                      </div>
                    </div>
                  </Col>
                  <NotaDinasRilisUpload
                    ref={notaDinasRilisRef}
                    procurementId={ids}
                    token={token}
                    path={form.documentNotaDinasRilis}
                    url={form.documentNotaDinasRilisUrl}
                    editable={hpsNotAvailable}
                    error={notaDinasRilisError}
                    onUploaded={async () => {
                      setNotaDinasRilisError('');
                      await onRefreshForm?.();
                    }}
                  />
                </>
              </Row>
            </CardBody>
          </Card>

          <Card className='border shadow-sm'>
            <CardBody>
              <Row>
                {hpsNotAvailable && (
                  <Col lg={12}>
                    <Alert
                      color='info'
                      className='d-flex align-items-start gap-2 mt-2'
                    >
                      <i className='ri-error-warning-line flex-shrink-0 mt-1'></i>
                      <span className='small'>
                        The detailed procurement plan information below can be
                        downloaded in Excel format to be used as an attachment
                        in the submission of the HPS Alpha Request Memorandum.
                      </span>
                    </Alert>
                  </Col>
                )}
                <Col sm={12}>
                  <div
                    color='dark'
                    className='d-flex align-items-center mb-2  '
                  >
                    <div className='flex-grow-1'>
                      <h5 className='mb-1'>Parcel</h5>
                      <p className='text-muted mb-2 fs-12 '>
                        Bisa jadi Kalimat tambahan untuk membantu section
                        mengenai apa ini
                      </p>
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <ActionButton
                        variant='secondary'
                        size='sm'
                        icon='ri-download-2-line'
                        onClick={
                          !LPG_PRODUCT.includes(form.productNameId)
                            ? handleDownloadParcel
                            : handleDwnParcelLpg
                        }
                        loading={isDownloadingParcel}
                      >
                        {isDownloadingParcel
                          ? 'Downloading...'
                          : 'Download Parcel'}
                      </ActionButton>

                      {alphaEditable ? (
                        <ActionButton
                          variant='primary'
                          size='sm'
                          icon='ri-upload-2-line'
                          onClick={
                            !LPG_PRODUCT.includes(form.productNameId)
                              ? toggleUploadNonLPG
                              : toggleUploadAlphaHps
                          }
                        >
                          Upload Parcel
                        </ActionButton>
                      ) : null}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xl={12}>
                  <div className='mb-1 '>
                    {/* <CardBody> */}
                    {isLoading ? (
                      <SkeletonCard lines={4} />
                    ) : error ? (
                      <Col lg={12} className='text-center'>
                        <div className='text-center'>
                          <div className='alert alert-danger'>{error}</div>
                        </div>
                      </Col>
                    ) : (
                      <>
                        {qtyDeliveryData.items.length > 0 ? (
                          qtyDeliveryData.items.map((data, index) => (
                            <Col sm={12} key={`${data.id}-${index}`}>
                              {LPG_PRODUCT.includes(form.productNameId) ? (
                                <HpsParcelSectionWithData
                                  parcels={[data]}
                                  procurement={form}
                                  token={token}
                                  isReadOnly
                                  isAlphaEditable={alphaEditable}
                                  alphaUploadVersion={alphaUploadVersion}
                                />
                              ) : (
                                <Card className='border shadow-sm mb-3'>
                                  <CardBody>
                                    <ParcelField
                                      parcel={{
                                        ...data,
                                        ...mapParcelPricingDisplay(data),
                                      }}
                                    />
                                    <ModeDeliveryTableFragment
                                      procurementId={ids}
                                      quantityDeliveryTermId={data.id}
                                      token={token}
                                      infoPrompt={data.infoPrompt}
                                      refreshKey={
                                        nonLPGUploadVersion + alphaUploadVersion
                                      }
                                      isEditable={alphaEditable}
                                    />
                                  </CardBody>
                                </Card>
                              )}
                            </Col>
                          ))
                        ) : (
                          <Col lg={12} className='text-center'>
                            <h5 className='fs-15'>
                              <i className='ri-database-2-line me-2'></i>
                              No data available
                            </h5>
                          </Col>
                        )}
                      </>
                    )}
                    {/* </CardBody> */}
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <UploadAlphaHpsModal
            isOpen={isUploadAlphaHpsOpen}
            toggle={toggleUploadAlphaHps}
            procurementId={ids}
            token={token}
            onUploaded={() => setAlphaUploadVersion(v => v + 1)}
          />

          <UploadNonLPGModal
            isOpen={isUploadNonLPGOpen}
            toggle={toggleUploadNonLPG}
            token={token}
            onUploaded={() => setNonLPGUploadVersion(v => v + 1)}
          />

          {hpsNotAvailable ? (
            <div className='req-actionbar flex-wrap'>
              <ActionButton
                variant='primary'
                icon='ri-login-box-line'
                className='ms-auto'
                onClick={handleConfirmProcess}
              >
                Submit HPS Request
              </ActionButton>
            </div>
          ) : (
            <HpsRevisePanel
              procurementId={ids}
              role='Hps'
              canSend={hpsRevise}
              statusKind={getHpsStatusKind(form.statusHPS2)}
              onSent={onRefreshForm}
            />
          )}
        </div>
      </Row>
    </React.Fragment>
  );
};

export default RequirementTab;
